import { useState, useEffect } from 'react';
import { apiService } from '@/lib/api-functions/api-service';
import { Version } from '@/lib/stores/storeTypes';

export interface MigrationChange {
  hasChanges: boolean;
  addedSections: Array<{ id: string; title: string }>;
  removedSections: Array<{ id: string; title: string }>;
  modifiedSections: Array<{ id: string; oldTitle: string; newTitle: string }>;
  addedFields: Array<{
    sectionId: string;
    sectionTitle: string;
    fieldId: string;
    fieldLabel: string;
    fieldType: string;
    field?: any; // Complete field definition from blueprint
  }>;
  removedFields: Array<{
    sectionId: string;
    sectionTitle: string;
    fieldId: string;
    fieldLabel: string;
    fieldType: string;
    field?: any; // Complete field definition
  }>;
  modifiedFields: Array<{
    sectionId: string;
    sectionTitle: string;
    fieldId: string;
    fieldLabel: string;
    oldFieldLabel: string;
    oldFieldType: string;
    newFieldType: string;
    changes: string[];
    oldField?: any; // Complete old field definition
    newField?: any; // Complete new field definition
  }>;
}

export interface MigrationPreviewData {
  object: {
    id: string;
    name: string;
    currentVersion: Version;
    currentBlueprintVersion: Version;
    data: any[];
  };
  blueprint: {
    id: string;
    name: string;
    latestVersion: Version;
  };
  preview: {
    currentVersion: Version;
    targetVersion: Version;
    changes: MigrationChange;
    currentStructure: any[];
    targetStructure: any | null;
    structureDiff: any;
  };
}

export interface UseMigrationPreviewReturn {
  previewData: MigrationPreviewData | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

/**
 * Hook to fetch migration preview data for an object
 * @param objectId - The ID of the object to preview migration for
 * @param targetVersion - Optional target version to migrate to (defaults to latest)
 */
export function useMigrationPreview(
  objectId: string | undefined,
  targetVersion?: Version
): UseMigrationPreviewReturn {
  const [previewData, setPreviewData] = useState<MigrationPreviewData | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const fetchPreview = async () => {
    if (!objectId) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await apiService.fetchMigrationPreview(objectId, targetVersion);

      if (response.success && response.data) {
        setPreviewData(response.data);
        setError(null);
      } else {
        throw new Error(response.error || 'Failed to fetch migration preview');
      }
    } catch (err: any) {
      console.error('[useMigrationPreview] Error fetching preview:', err);
      setError(err.message || 'Failed to fetch migration preview');
      setPreviewData(null);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchPreview();
  }, [objectId, targetVersion?.major, targetVersion?.minor, targetVersion?.patch]);

  return {
    previewData,
    isLoading,
    error,
    refetch: fetchPreview,
  };
}

